import FlexBox from "components/atoms/FlexBox";
import Icon from "components/atoms/Icon";
import SortIcon from "components/molecules/SortIcon";
import { grayScale } from "components/styles";
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { isMacOs } from "react-device-detect";
import FlexBoxItem from "components/atoms/FlexBoxItem";
import IconLabelButton from "components/molecules/IconLabelButton";

import "./styles.css";

type TableColumn = {
  values: {
    value: string;
    sortField?: string;
    sortType?: "string" | "number";
  }[];
  rowSpan?: number;
  colSpan?: number;
  style?: React.CSSProperties;
  width?: number;
  className?: string;
  sticky?: boolean;
};

export type TableHeader = {
  rows: TableColumn[];
}[];

type Alignment = "left" | "center" | "right";

export type TableDataRow = {
  [key: string]: {
    values: {
      value: any;
      linkTo?: string;
    }[];
    width?: number;
    alignment?: Alignment;
    sticky?: boolean;
    style?: React.CSSProperties;
    className?: string;
    rowSpan?: number;
    colSpan?: number;
    type?: "normal" | "subtotal" | "total"; // 行の種類: 通常、小計、合計
  };
};

type TableProps = {
  minHeight?: string;
  maxHeight?: string;
  headerRows: TableHeader;
  dataRows: TableDataRow[];
  sortable: boolean;
  showExportCSV?: boolean;
  showExportExcel?: boolean;
  onExportCSV?: (data: any[], headerInformation: any[][]) => void;
  onExportExcel?: (
    data: any[],
    headerInformation: any[][],
    mergedRegions: number[][]
  ) => void;
};

// JSONB形式のデータを変換するヘルパー関数
export const convertJsonbData = (data: any): any => {
  if (data && typeof data === "object") {
    // JSONB形式のチェック（anyを使用して型エラーを回避）
    const jsonbData = data as any;
    if (
      jsonbData.type === "jsonb" &&
      jsonbData.value &&
      typeof jsonbData.value === "string"
    ) {
      try {
        const parsedData = JSON.parse(jsonbData.value);
        return {
          ...parsedData,
          // valuesが存在しない場合のデフォルト値
          values: parsedData.values || [{ value: parsedData.value || "" }]
        };
      } catch (error) {
        console.error("JSONB parsing error:", error);
        return data;
      }
    }
  }
  return data;
};

const GenericTable: React.FC<TableProps> = ({
  minHeight = "10%",
  maxHeight = "calc(100vh - 300px)",
  headerRows,
  dataRows,
  sortable,
  showExportCSV,
  showExportExcel,
  onExportCSV,
  onExportExcel
}) => {
  const [sortedData, setSortedData] = useState<TableDataRow[]>([]);
  const [sortConfig, setSortConfig] = useState<{
    field?: string;
    direction: "asc" | "desc";
  }>({
    field: headerRows?.[0]?.rows?.[0].values?.[0].sortField ?? "",
    direction: "asc"
  });

  useEffect(() => {
    setSortedData(dataRows);
  }, [dataRows]);

  // 固定列のleft位置を計算
  const stickyHeaderLeftPositions: (number | "auto")[][] = new Array(
    headerRows.length
  );

  headerRows.forEach((row, rowIndex) => {
    let cumulativeLeft = 0;
    stickyHeaderLeftPositions[rowIndex] = []; // 各行の配列を初期化

    row.rows.forEach((col, colIndex) => {
      if (col.sticky) {
        stickyHeaderLeftPositions[rowIndex][colIndex] = cumulativeLeft;
        cumulativeLeft += (col.width ? col.width : 100) + 3; // デフォルト幅を100pxと仮定
      } else {
        stickyHeaderLeftPositions[rowIndex][colIndex] = "auto";
      }
    });
  });

  // 固定列のleft位置を計算
  const stickyRowLeftPositions: (number | "auto")[][] = new Array(
    dataRows.length
  );

  dataRows.forEach((row, rowIndex) => {
    let cumulativeLeft = 0;
    stickyRowLeftPositions[rowIndex] = []; // 各行の配列を初期化

    Object.keys(row).map((col, colIndex) => {
      const cells = row[col];

      if (cells.sticky) {
        stickyRowLeftPositions[rowIndex][colIndex] = cumulativeLeft;
        cumulativeLeft += (cells.width ? cells.width : 100) + 3; // デフォルト幅を100pxと仮定
      } else {
        stickyRowLeftPositions[rowIndex][colIndex] = "auto";
      }
    });
  });

  const handleSort = (sortField: string, sortType: "string" | "number") => {
    if (!sortable || !sortField) return;

    const direction =
      sortConfig.field === sortField && sortConfig.direction === "asc"
        ? "desc"
        : "asc";

    const sorted = [...sortedData].sort((a, b) => {
      const aValue = a[sortField]?.values?.[0]?.value;
      const bValue = b[sortField]?.values?.[0]?.value;

      // undefined値のチェックを追加
      if (sortType === "number") {
        // 数値の場合、undefinedは0として扱う。%やカンマ区切りにも対応
        const parseNumber = (val: any) => {
          if (val === undefined || val === null) return 0;
          // 文字列化し、カンマと%を除去
          const cleaned = String(val)
            .replace(/,/g, "")
            .replace(/%/g, "");
          const num = Number(cleaned);
          return isNaN(num) ? 0 : num;
        };
        const aNum = parseNumber(aValue);
        const bNum = parseNumber(bValue);
        return direction === "asc" ? aNum - bNum : bNum - aNum;
      } else {
        // 文字列の場合、undefinedは空文字として扱う
        const aStr = aValue !== undefined ? String(aValue) : "";
        const bStr = bValue !== undefined ? String(bValue) : "";
        return direction === "asc"
          ? aStr.localeCompare(bStr)
          : bStr.localeCompare(aStr);
      }
    });

    setSortedData(sorted);
    setSortConfig({ field: sortField, direction });
  };

  const getAlignmentClass = (alignment?: Alignment) => {
    switch (alignment) {
      case "center":
        return "text-center";
      case "right":
        return "text-right";
      default:
        return "text-left";
    }
  };

  /** ヘッダー情報生成 */
  const generateHeaderRows = (type: "csv" | "excel") => {
    // 全体の列数を決定する
    let totalColumns = 0;
    headerRows[0].rows.forEach(cell => {
      totalColumns += cell.colSpan || 1;
    });

    // ヘッダー行の最大値数を調べる
    let maxValuesLength = 0;
    headerRows.forEach(row => {
      row.rows.forEach(cell => {
        if (cell.values && cell.values.length > maxValuesLength) {
          maxValuesLength =
            maxValuesLength < cell.values.length
              ? cell.values.length
              : maxValuesLength;
        }
      });
    });

    // セルの占有状態を追跡するためのマップ
    const occupiedCellMap: { [key: string]: boolean } = {};

    // ヘッダー用のグリッド（行数 = headerRows.length * maxValuesLength）
    const grid: any[][] = [];

    // ヘッダー行ごとに処理
    headerRows.forEach((headerRow, headerRowIndex) => {
      // 各セルのvalues分だけ行を追加
      for (let valueIndex = 0; valueIndex < maxValuesLength; valueIndex++) {
        const gridRow: any[] = Array(totalColumns).fill("");
        grid.push(gridRow);

        let colIndex = 0;
        // 各セルについて処理
        headerRow.rows.forEach(cell => {
          // 既に占有されているセルをスキップ
          while (
            occupiedCellMap[
              `${headerRowIndex * maxValuesLength + valueIndex}-${colIndex}`
            ] &&
            colIndex < totalColumns
          ) {
            colIndex++;
          }

          if (colIndex >= totalColumns) return;

          const colSpan = cell.colSpan || 1;
          const rowSpan = cell.rowSpan || 1;

          // セルの値を設定（values配列のindexに対応する値）
          if (cell.values && valueIndex < cell.values.length) {
            const cellValue = cell.values[valueIndex].value;
            if (type === "csv" && typeof cellValue === "string") {
              // CSVの場合は改行コードを空白に置換
              gridRow[colIndex] = cellValue.replace(/\r?\n/g, "");
            } else {
              gridRow[colIndex] = cellValue;
            }
          }

          // colSpanが2以上の場合、水平方向に空セルを設定
          for (let c = 1; c < colSpan && colIndex + c < totalColumns; c++) {
            gridRow[colIndex + c] = "";
            occupiedCellMap[
              `${headerRowIndex * maxValuesLength + valueIndex}-${colIndex + c}`
            ] = true;
          }

          // rowSpanが2以上の場合、垂直方向に占有状態を設定
          if (rowSpan > 1) {
            for (let r = 1; r < rowSpan; r++) {
              const targetRowIndex =
                headerRowIndex * maxValuesLength + valueIndex + r;
              for (let c = 0; c < colSpan && colIndex + c < totalColumns; c++) {
                occupiedCellMap[`${targetRowIndex}-${colIndex + c}`] = true;
              }
            }
          }

          // 次の列へ
          colIndex += colSpan;
        });
      }
    });

    return grid;
  };

  /** headerRowsからExcel用のセル結合情報を生成 */
  const generateMergedRegions = (): number[][] => {
    const mergedRegions: number[][] = [];
    // Excelでのヘッダー行開始位置（通常はExcelシートの先頭に数行ヘッダーがある）
    const headerRowOffset = 4;

    // ヘッダー行の処理 - 各行の列インデックスを追跡
    const headerColIndices: Record<number, number> = {};

    // 各ヘッダー行について処理
    headerRows.forEach((headerRow, headerRowIndex) => {
      headerColIndices[headerRowIndex] = 0;
      let colIndex = 0;

      // 行内の各セルについて処理
      headerRow.rows.forEach(cell => {
        if (!cell.values || cell.values.length === 0) return; // valuesがないものは除外
        const rowSpan = cell.rowSpan || 1;
        const colSpan = cell.colSpan || 1;

        // colSpanかrowSpanが2以上の場合のみ結合情報を追加
        if (colSpan > 1 || rowSpan > 1) {
          // Excel形式の結合情報: [開始行, 終了行, 開始列, 終了列]
          mergedRegions.push([
            headerRowOffset + headerRowIndex,
            headerRowOffset + headerRowIndex + rowSpan - 1,
            colIndex,
            colIndex + colSpan - 1
          ]);
        }

        colIndex += colSpan;
      });

      headerColIndices[headerRowIndex] = colIndex;
    });

    // データ行の処理
    const dataRowOffset = headerRowOffset + headerRows.length; // データ行の開始位置
    const dataColCounts: any = {}; // 各行における列の使用状況を追跡

    // 各データ行について処理
    sortedData.forEach((row, rowIndex) => {
      let colIndex = 0;

      // 行内の各セルについて処理
      Object.keys(row).forEach(col => {
        const cell = row[col];
        if (!cell.values || cell.values.length === 0) return; // valuesがないものは除外
        // 既に使用されているセルをスキップ（前の行のrowSpanによってこの位置が占有されている場合）
        while (dataColCounts[`${rowIndex}-${colIndex}`]) {
          colIndex++;
        }

        const rowSpan = cell.rowSpan || 1;
        const colSpan = cell.colSpan || 1;

        // colSpanかrowSpanが2以上の場合のみ結合情報を追加
        if (colSpan > 1 || rowSpan > 1) {
          // Excel形式の結合情報: [開始行, 終了行, 開始列, 終了列]
          mergedRegions.push([
            dataRowOffset + rowIndex,
            dataRowOffset + rowIndex + rowSpan - 1,
            colIndex,
            colIndex + colSpan - 1
          ]);

          // rowSpanが2以上の場合、後続の行のセルを「使用済み」としてマーク
          if (rowSpan > 1) {
            for (let r = 1; r < rowSpan; r++) {
              for (let c = 0; c < colSpan; c++) {
                dataColCounts[`${rowIndex + r}-${colIndex + c}`] = true;
              }
            }
          }
        }

        colIndex += colSpan;
      });
    });

    return mergedRegions;
  };

  return (
    <div>
      {(showExportCSV || showExportExcel) && (
        <FlexBox>
          {showExportCSV && (
            <FlexBoxItem>
              <IconLabelButton
                onClick={() => {
                  if (onExportCSV) {
                    // セル結合を考慮したデータ構造を作成
                    const formattedData: any[] = [];

                    // 行と列の占有状態を追跡
                    const occupiedCells: { [key: string]: boolean } = {};

                    // 各データ行について処理
                    sortedData.forEach((row, rowIndex) => {
                      // 各行の最大values数を調べる
                      let maxValuesLength = 0;
                      Object.keys(row).forEach(col => {
                        const valuesLength = row[col]?.values?.length || 0;
                        if (valuesLength > maxValuesLength) {
                          maxValuesLength = valuesLength;
                        }
                      });

                      // 各value行について処理
                      for (
                        let valueIndex = 0;
                        valueIndex < maxValuesLength;
                        valueIndex++
                      ) {
                        const rowData: any[] = [];
                        let colIndex = 0;

                        // 各列について処理
                        Object.keys(row).forEach(col => {
                          // 既に占有されているセルをスキップ
                          while (
                            occupiedCells[
                              `${rowIndex + valueIndex}-${colIndex}`
                            ]
                          ) {
                            colIndex++;
                            rowData.push(""); // 占有されているセルには空文字を追加
                          }

                          // セルの情報を取得
                          const cell = row[col];
                          const rowSpan = cell.rowSpan || 1;
                          const colSpan = cell.colSpan || 1;

                          // valueIndexに対応する値を取得（なければ空文字）
                          if (cell && Array.isArray(cell.values)) {
                            const value =
                              cell.values[valueIndex]?.value !== undefined
                                ? cell.values[valueIndex].value
                                : "";
                            // 値を追加
                            rowData.push(value);
                            // colSpan分の空セルを追加
                            for (let c = 1; c < colSpan; c++) {
                              rowData.push("");
                            }
                          }

                          // rowSpanとcolSpanに基づいて、将来の行での占有状態を更新
                          if (rowSpan > 1) {
                            for (let r = 1; r < rowSpan; r++) {
                              for (let c = 0; c < colSpan; c++) {
                                if (valueIndex + r < maxValuesLength) {
                                  occupiedCells[
                                    `${rowIndex + valueIndex + r}-${colIndex +
                                      c}`
                                  ] = true;
                                }
                              }
                            }
                          }

                          // 列位置を更新
                          colIndex += colSpan;
                        });

                        formattedData.push(rowData);
                      }
                    });

                    onExportCSV(formattedData, generateHeaderRows("csv"));
                  }
                }}
                iconType="download"
                text=" CSV出力"
              />
            </FlexBoxItem>
          )}
          {showExportExcel && (
            <FlexBoxItem>
              <IconLabelButton
                onClick={() => {
                  if (onExportExcel) {
                    // セル結合を考慮したデータ構造を作成
                    const formattedData: any[] = [];

                    // 行と列の占有状態を追跡
                    const occupiedCells: { [key: string]: boolean } = {};

                    // 各データ行について処理
                    sortedData.forEach((row, rowIndex) => {
                      // 各行の最大values数を調べる
                      let maxValuesLength = 0;
                      Object.keys(row).forEach(col => {
                        const valuesLength = row[col]?.values?.length || 0;
                        if (valuesLength > maxValuesLength) {
                          maxValuesLength = valuesLength;
                        }
                      });

                      // 各value行について処理
                      for (
                        let valueIndex = 0;
                        valueIndex < maxValuesLength;
                        valueIndex++
                      ) {
                        const rowData: any[] = [];
                        let colIndex = 0;

                        // 各列について処理
                        Object.keys(row).forEach(col => {
                          // 既に占有されているセルをスキップ
                          while (
                            occupiedCells[
                              `${rowIndex + valueIndex}-${colIndex}`
                            ]
                          ) {
                            colIndex++;
                            rowData.push(""); // 占有されているセルには空文字を追加
                          }

                          // セルの情報を取得
                          const cell = row[col];
                          const rowSpan = cell.rowSpan || 1;
                          const colSpan = cell.colSpan || 1;

                          // valueIndexに対応する値を取得（なければ空文字）
                          if (cell && Array.isArray(cell.values)) {
                            const value =
                              cell.values[valueIndex]?.value !== undefined
                                ? cell.values[valueIndex].value
                                : "";
                            // 値を追加
                            rowData.push(value);
                            // colSpan分の空セルを追加
                            for (let c = 1; c < colSpan; c++) {
                              rowData.push("");
                            }
                          }

                          // rowSpanとcolSpanに基づいて、将来の行での占有状態を更新
                          if (rowSpan > 1) {
                            for (let r = 1; r < rowSpan; r++) {
                              for (let c = 0; c < colSpan; c++) {
                                if (valueIndex + r < maxValuesLength) {
                                  occupiedCells[
                                    `${rowIndex + valueIndex + r}-${colIndex +
                                      c}`
                                  ] = true;
                                }
                              }
                            }
                          }

                          // 列位置を更新
                          colIndex += colSpan;
                        });

                        formattedData.push(rowData);
                      }
                    });

                    // EXCEL出力の場合、セル結合情報を生成して渡す
                    const headerData = generateHeaderRows("excel");
                    const mergedRegions = generateMergedRegions();

                    // ヘッダー情報とmergedRegionsをExcel出力に渡す
                    onExportExcel(formattedData, headerData, mergedRegions);
                  }
                }}
                iconType="download"
                text=" EXCEL出力"
              />
            </FlexBoxItem>
          )}
        </FlexBox>
      )}

      <div style={{ margin: "15px" }}>
        <div
          className="templateTable newDesignTable genericTable"
          style={{
            height: "auto",
            minHeight: minHeight,
            maxHeight: maxHeight,
            paddingBottom: isMacOs ? "35px" : "1px"
          }}
        >
          <table className="table table-bordered text-nowrap">
            <thead>
              {headerRows.map((headerRow, rowIndex) => (
                <tr key={rowIndex}>
                  {headerRow.rows.map((col, colIndex) => (
                    <th
                      key={colIndex}
                      rowSpan={col.rowSpan}
                      colSpan={col.colSpan}
                      style={{
                        position: "sticky",
                        top: 34 * rowIndex,
                        width: col.width ? col.width : "100px",
                        maxWidth: col.width ? col.width : "100px",
                        minWidth: col.width ? col.width : "100px",
                        zIndex: col.sticky ? 2 : "auto",
                        left: stickyHeaderLeftPositions[rowIndex][colIndex],
                        ...col.style
                      }}
                      className={`text-center stickyStyle  ${
                        col.sticky && colIndex === 0 ? "fristColSticky" : ""
                      } ${col.className || ""}`}
                      onClick={() =>
                        col.values.length === 1 &&
                        col.values[0]?.sortField &&
                        handleSort(
                          col.values[0].sortField,
                          col.values[0].sortType || "string"
                        )
                      }
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "center",
                          position: "relative"
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            width: "100%",
                            flexDirection: "column",
                            justifyContent: "center",
                            position: "relative"
                          }}
                        >
                          {col.values.map((cell, cellIndex) => (
                            <div
                              key={cellIndex}
                              style={{
                                borderBottom:
                                  cellIndex !== col.values.length - 1
                                    ? "1px dashed #ddd"
                                    : undefined,
                                whiteSpace: "pre-wrap",
                                minHeight: "19px"
                              }}
                            >
                              {cell.value}
                            </div>
                          ))}
                        </div>
                        {sortable &&
                          col.values.length === 1 &&
                          col.values[0]?.sortField && (
                            <React.Fragment>
                              {col.values[0].sortField === sortConfig.field ? (
                                <span>
                                  <SortIcon
                                    isSortedDesc={
                                      sortConfig.direction === "desc"
                                    }
                                  />
                                </span>
                              ) : (
                                <Icon
                                  type="sortDefault"
                                  color={grayScale.gray100}
                                  size="25px"
                                />
                              )}
                            </React.Fragment>
                          )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {sortedData.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {Object.keys(row).map((col, colIndex) => {
                    // typeプロパティはスキップ
                    if (col === "type") return null;

                    const cells = row[col];

                    if (Object.keys(cells).length === 0) return null;

                    return (
                      <td
                        key={colIndex}
                        colSpan={cells.colSpan}
                        rowSpan={cells.rowSpan}
                        className={`${getAlignmentClass(cells.alignment)} ${
                          cells.sticky ? "stickyStyle" : ""
                        } ${
                          cells.sticky && colIndex === 0 ? "fristColSticky" : ""
                        } ${cells.className || ""}`}
                        style={{
                          zIndex: cells.sticky ? 1 : "auto",
                          width: cells.width ? cells.width : "100px",
                          maxWidth: cells.width ? cells.width : "100px",
                          minWidth: cells.width ? cells.width : "100px",
                          left:
                            stickyRowLeftPositions[rowIndex]?.[colIndex] ??
                            "auto",
                          // typeに応じた背景色の設定
                          ...(cells.type === "subtotal" ||
                          cells.type === "total"
                            ? { backgroundColor: "#ffeeee" }
                            : {}),
                          ...cells.style
                        }}
                      >
                        {cells.values.map((cell, cellIndex) => (
                          <div
                            key={cellIndex}
                            style={{
                              borderBottom:
                                cellIndex !== cells.values.length - 1
                                  ? "1px dashed #ddd"
                                  : undefined,
                              minHeight: "19px"
                            }}
                          >
                            {cell.linkTo ? (
                              <Link to={cell.linkTo}>{cell.value}</Link>
                            ) : (
                              <span>{cell.value}</span>
                            )}
                          </div>
                        ))}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default GenericTable;
